"use client";
import { ExpandIcon } from "@gettactic/components";
import { cn, useDisclosure } from "@gettactic/helpers";
import { useAuthenticated } from "@gettactic/hooks";
import { SurfaceCardShellPaddingMd } from "@cmp-src/data-display/Card/classes";
import { IconButton } from "@cmp-src/primitives/icon-button/IconButton";
import { MapFiltersCard } from "@/features/map/components/MapFiltersCard/MapFiltersCard";
import { MapReserveCard } from "@/features/map/components/MapReserveCard/MapReserveCartd";
import { MapUserCard } from "@/features/map/components/MapUserCard/MapUserCard";
import { useMapModalUrlSync } from "@/features/dashboard/hooks/useMapModalUrlSync";
import { useMap } from "@/features/map/MapProvider";
import { MapView } from "@/features/map/components/MapView/MapView";
import { useMapViewSvgEdit } from "@/features/map/components/MapView/MapViewSvg/useMapViewSvgEdit";
import { useMapPageUrlSync } from "@/features/map/components/Page/useMapPageUrlSync";
import { addMinutes, format } from "date-fns";
import { X } from "lucide-react";
import React from "react";

const classes = {
  Wrapper: "map-page h-full relative",
  FiltersSidebar: `filters-sidebar md:ml-1 md:ml-0 ${SurfaceCardShellPaddingMd} md:w-[440px] md:h-full md:relative`,
  MapWrapper: "absolute inset-0 overflow-hidden",
  NavigationButtons:
    "h-8 w-8 rounded-full bg-background/90 backdrop-blur-sm hover:bg-background text-neutral-500",
  NavigationButtonsIcon: "h-4 w-4"
};

import { useReserve } from "@/features/reserve/providers/context";
import { ReserveModalDisclosureOptions } from "@/features/reserve/providers/types";
import { ISchedule } from "@gettactic/api";

type MapPageContentProps = {
  isModal?: boolean;
  onSuccess?: () => void;
  onClose?: () => void;
  isOnReservationModal?: boolean;
  duplicateData?: ReserveModalDisclosureOptions;
  editSchedule?: ISchedule;
};

export const MapPageContent: React.FC<MapPageContentProps> = ({
  isModal,
  onSuccess,
  onClose,
  isOnReservationModal,
  duplicateData,
  editSchedule
}) => {
  const dayViewDisclosure = useDisclosure({ defaultIsOpen: false });
  const {
    fullscreenMapDisclosure,
    viewingDivision,
    startDate,
    endDate,
    floor,
    floors,
    setFloor,
    timeMode,
    defaultTab: section,
    filterQuery,
    mapInstanceId,
    changeStartDate,
    changeEndTime
  } = useMap();
  const editor = useMapViewSvgEdit();
  const {
    userContext: { currentOffice, authenticatedUser }
  } = useAuthenticated();

  const { openReserveModal, closeReserveModal, isReserveOpen } = useReserve();

  const handleModalClose = () => {
    if (isReserveOpen) {
      // User clicked X button - reset to initial time
      const initialTime = closeReserveModal();
      if (initialTime) {
        changeStartDate(initialTime.start || null);
        changeEndTime(
          addMinutes(
            initialTime.start || new Date(),
            initialTime.duration || 0
          ) || null
        );
      }
      setTimeout(() => {
        onClose?.();
      }, 300);
    } else {
      onClose?.();
    }
  };

  const handleExternalLink = () => {
    const params = new URLSearchParams();

    if (currentOffice?.id) params.set("officeId", currentOffice.id);
    if (floor?.id) params.set("floorId", floor.id);

    if (startDate) {
      const dateStr = `${format(startDate, "yyyy-MM-dd")}T${format(startDate, "HH:mm:ss")}`;
      params.set("date", dateStr);
      params.set("startTime", dateStr);
    }

    if (endDate) {
      const dateStr = `${format(endDate, "yyyy-MM-dd")}T${format(endDate, "HH:mm:ss")}`;
      params.set("endTime", dateStr);
    }

    if (timeMode) params.set("timeMode", timeMode);
    if (section) params.set("section", section);
    if (filterQuery) params.set("searchText", filterQuery);

    // Construct /map URL with all current params
    const url = `/map?${params.toString()}`;
    window.open(url, "_blank");
  };

  React.useEffect(() => {
    if (duplicateData) {
      // Force save initial time for duplicate data
      openReserveModal(duplicateData, false, true);
    }
  }, [duplicateData]);

  // Navigate the map to the correct floor when duplicateData specifies one
  React.useEffect(() => {
    if (!duplicateData?.floorId || !floors?.length) return;
    const targetFloor = floors.find((f) => f.id === duplicateData.floorId);
    if (targetFloor && targetFloor.id !== floor?.id) {
      setFloor(targetFloor);
    }
  }, [duplicateData?.floorId, floors]);

  // Use different URL sync hooks based on context
  // Call hooks unconditionally, but they'll handle their own logic
  useMapPageUrlSync(!isModal);

  // We enable time sync even in reservation modal to show correct URL
  useMapModalUrlSync(isModal, false);

  const isMapEditing =
    editor?.isResourcePanelEditorOpen || editor?.isMapBuilderEditorOpen;

  return (
    <div className={classes.Wrapper}>
      {isModal && (
        <div className="absolute top-4 right-4 z-50 flex">
          <IconButton
            icon={<ExpandIcon />}
            variant="ghost"
            size="sm"
            iconClassName={classes.NavigationButtonsIcon}
            className={classes.NavigationButtons}
            onClick={handleExternalLink}
            aria-label="Open in new window"
          />
          <IconButton
            icon={X}
            variant="ghost"
            size="sm"
            iconClassName={classes.NavigationButtonsIcon}
            className={classes.NavigationButtons}
            onClick={handleModalClose}
            aria-label="Close"
          />
        </div>
      )}
      <div className={classes.MapWrapper}>
        <MapView
          isOnReservationModal={isOnReservationModal}
          embedded={false}
          editor={editor}
          isModal={isModal}
        />
      </div>
      {!fullscreenMapDisclosure.isOpen &&
        !editor.isResourcePanelEditorOpen &&
        !editor?.isMapBuilderEditorOpen && (
          <div
            className={cn(
              `${classes.FiltersSidebar} ${isModal ? " md:m-0 md:rounded-none md:p-0" : ""}`,
              // Campus mode: the map runs full-bleed underneath, so the
              // sidebar column must shrink-wrap and stop swallowing clicks —
              // the card itself re-enables pointer events.
              viewingDivision && "md:pointer-events-none md:h-auto"
            )}
          >
            <div
              id={`map-daily-view-card-holder-${mapInstanceId.replace(/:/g, "")}`}
            />
            {!isMapEditing && (
              <MapFiltersCard
                isModal={isModal}
                dayViewDisclosure={dayViewDisclosure}
              />
            )}
            <MapUserCard />
            <MapReserveCard
              isModal={isModal}
              isOnReservationModal={isOnReservationModal}
              dayViewDisclosure={dayViewDisclosure}
              onSuccess={onSuccess}
              onClose={onClose}
              editSchedule={editSchedule}
            />
          </div>
        )}
    </div>
  );
};
